import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import authService from '../../services/auth.service';
import { ManagedUser, UpdateDefaultOrganization } from '../../types/auth.types';
import { useCompanyOptions, useCompanyTeams, useTeamDepartments } from '../../hooks/useOrgQueries';
import { LedgerDialog, LedgerField, LedgerSelect, LedgerSelectOption } from '../ui';

// Every level is optional — clearing all three is a valid "no default" state.
const schema = yup.object({
  company: yup.string().defined().default(''),
  team: yup.string().defined().default(''),
  department: yup.string().defined().default(''),
});

type DefaultOrgFormValues = yup.InferType<typeof schema>;

const defaultValues: DefaultOrgFormValues = {
  company: '',
  team: '',
  department: '',
};

const NONE_OPTION: LedgerSelectOption = { value: '', label: '— None —' };

interface DefaultOrgDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: ManagedUser;
}

interface DefaultOrgErrorData {
  detail?: string;
  default_company?: string[];
  default_team?: string[];
  default_department?: string[];
}

export const DefaultOrgDialog: React.FC<DefaultOrgDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const companyTriggerRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  const saveDefaults = useMutation({
    mutationFn: (data: UpdateDefaultOrganization) => authService.updateDefaultOrganization(data),
    onSuccess: () => {
      // The user rows and detail denormalize the default-org names.
      queryClient.invalidateQueries({ queryKey: ['users-page'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail'] });
    },
  });
  const saving = saveDefaults.isPending;

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<DefaultOrgFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  const companyId = watch('company');
  const teamId = watch('team');

  const companiesQuery = useCompanyOptions(open);
  const teamsQuery = useCompanyTeams(companyId || null);
  const departmentsQuery = useTeamDepartments(teamId || null);

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    reset({
      company: user.defaultCompanyId ?? '',
      team: user.defaultTeamId ?? '',
      department: user.defaultDepartmentId ?? '',
    });
  }, [user, open, reset]);

  const companyOptions: LedgerSelectOption[] = [
    NONE_OPTION,
    ...(companiesQuery.data ?? []).map((company) => ({ value: company.id, label: company.name })),
  ];
  const teamOptions: LedgerSelectOption[] = [
    NONE_OPTION,
    ...(teamsQuery.data ?? []).map((team) => ({ value: team.id, label: team.name })),
  ];
  const departmentOptions: LedgerSelectOption[] = [
    NONE_OPTION,
    ...(departmentsQuery.data ?? []).map((department) => ({
      value: department.id,
      label: department.name,
    })),
  ];

  // Child queries idle on skipToken until their parent is chosen, so only the
  // ones actually fetching may hold the submit button hostage.
  const optionsPending =
    companiesQuery.isPending ||
    (!!companyId && teamsQuery.isPending) ||
    (!!teamId && departmentsQuery.isPending);
  const optionsError =
    companiesQuery.isError ||
    (!!companyId && teamsQuery.isError) ||
    (!!teamId && departmentsQuery.isError);

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: DefaultOrgFormValues) => {
    setError(null);
    try {
      // Cleared levels go up as explicit nulls: axios drops undefined keys
      // from the JSON body, and the partial serializer treats a missing key
      // as no-change — only null actually clears a stored default.
      await saveDefaults.mutateAsync({
        defaultCompany: values.company || null,
        defaultTeam: values.team || null,
        defaultDepartment: values.department || null,
        userId: user.id,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      const data = (err as { response?: { data?: DefaultOrgErrorData } }).response?.data;
      const fieldMessage =
        data?.default_company?.[0] ?? data?.default_team?.[0] ?? data?.default_department?.[0];
      setError(fieldMessage || data?.detail || 'Failed to update default organization');
    }
  };

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Default Organization"
      size="md"
      preventClose={saving}
      initialFocus={companyTriggerRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="default-org-form"
            disabled={saving || isSubmitting || optionsPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="default-org-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(error || optionsError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load organizations'}
          </p>
        )}

        <LedgerField label="Company">
          <Controller
            name="company"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Company"
                value={field.value}
                options={companyOptions}
                onValueChange={(next) => {
                  // The children belong to the previous parent — clear them.
                  if (next !== field.value) {
                    setValue('team', '');
                    setValue('department', '');
                  }
                  field.onChange(next);
                }}
                disabled={companiesQuery.isPending}
                triggerRef={companyTriggerRef}
              />
            )}
          />
        </LedgerField>

        <LedgerField label="Team">
          <Controller
            name="team"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Team"
                value={field.value}
                options={teamOptions}
                onValueChange={(next) => {
                  if (next !== field.value) setValue('department', '');
                  field.onChange(next);
                }}
                disabled={!companyId || teamsQuery.isPending}
              />
            )}
          />
        </LedgerField>

        <LedgerField label="Department">
          <Controller
            name="department"
            control={control}
            render={({ field }) => (
              <LedgerSelect
                ariaLabel="Department"
                value={field.value}
                options={departmentOptions}
                onValueChange={field.onChange}
                disabled={!teamId || departmentsQuery.isPending}
              />
            )}
          />
        </LedgerField>
      </form>
    </LedgerDialog>
  );
};
